import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/auth.store";
import { useCashierStore } from "@/stores/cashier.store";
import type { PosSaleSlot } from "@/type/pos";
import { posSaleSlotHasWork } from "@/type/pos";

/**
 * The POS sale tabs, held OUTSIDE the page so they survive in-app navigation.
 *
 * `pages/pos/sell.vue` still owns the live sale (its own refs). Leaving the page
 * freezes that sale into its slot here; coming back restores it. So a cashier can
 * jump to Boarding or an invoice and return to every open sale exactly as it was.
 *
 * ⚠️ MEMORY ONLY, ON PURPOSE. Writing carts to localStorage is backend row data,
 * which `docs/README.frontend-caching-plan.md §11` forbids, and a restored cart
 * would carry prices that may have moved. A reload still loses the sales; the page
 * warns on `beforeunload` instead.
 *
 * ⚠️ NOT `<KeepAlive>` on the page either: a kept-alive POS keeps its barcode and
 * keydown listeners live on every other screen (see the phase-4 note in AGENT.md).
 *
 * ⚠️ SALES BELONG TO A USER + CASHIER PROFILE. Their price list, warehouse and
 * currency came from that profile, so a different user or a switched profile must
 * not inherit them — `claim` discards on an owner mismatch.
 */
export type PosSalesClaim = "fresh" | "restored" | "discarded";

const usePosSalesStore = defineStore("pos-sales", () => {
  const slots = ref<PosSaleSlot[]>([]);
  const activeSlotId = ref("");
  const owner = ref<string | null>(null);

  /**
   * Sales with something in them. Reads the frozen copies, which are current
   * whenever the POS page is not mounted — the only time anything outside it asks.
   */
  const openWorkCount = computed(() => slots.value.filter(posSaleSlotHasWork).length);

  function ownerKey() {
    const auth = useAuthStore();
    const cashier = useCashierStore();

    return `${auth.username ?? ""}|${cashier.activeProfileName ?? ""}`;
  }

  function seed(createSlot: () => PosSaleSlot) {
    const slot = createSlot();

    slots.value = [slot];
    activeSlotId.value = slot.id;
    owner.value = ownerKey();
  }

  /** Called by the POS page as it is set up. */
  function claim(createSlot: () => PosSaleSlot): PosSalesClaim {
    if (!slots.value.length) {
      seed(createSlot);

      return "fresh";
    }

    if (owner.value !== ownerKey()) {
      const hadWork = openWorkCount.value > 0;

      seed(createSlot);

      return hadWork ? "discarded" : "fresh";
    }

    if (!slots.value.some(slot => slot.id === activeSlotId.value))
      activeSlotId.value = slots.value[0].id;

    return "restored";
  }

  function reset() {
    slots.value = [];
    activeSlotId.value = "";
    owner.value = null;
  }

  return { slots, activeSlotId, owner, openWorkCount, claim, reset };
});

export default usePosSalesStore;
